import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Sparkles,
  Package,
  ArrowRight,
  ShoppingCart,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AISuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSuggestions = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await axios.get(`${API}/ai/suggestions`, {
        withCredentials: true,
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      if (!isRefresh) {
        toast.error("Failed to load AI suggestions");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-50 text-red-700 border-red-200";
      case "medium":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <Card className="rounded-sm border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        </CardContent>
      </Card>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Card
      className="rounded-sm border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50"
      data-testid="ai-suggestions-widget"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-lg font-semibold text-indigo-900">
              AI Recommendations
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchSuggestions(true)}
            disabled={refreshing}
            className="text-indigo-600 hover:text-indigo-800"
          >
            <RefreshCw
              className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-indigo-600 mb-4">
          {suggestions.length} smart reorder suggestion
          {suggestions.length > 1 ? "s" : ""} based on your inventory
        </p>
        <div className="space-y-3">
          {suggestions.slice(0, 4).map((suggestion) => (
            <div
              key={suggestion.suggestion_id}
              className="p-3 bg-white rounded-sm border border-indigo-100 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-slate-900">
                        {suggestion.data.item_name}
                      </p>
                      <Badge
                        className={`text-xs ${getPriorityColor(
                          suggestion.priority
                        )}`}
                      >
                        {suggestion.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500">
                      Current: {suggestion.data.current_stock}{" "}
                      {suggestion.data.unit} • Min: {suggestion.data.min_threshold}
                    </p>
                    <p className="text-sm text-indigo-700 mt-1">
                      <ShoppingCart className="h-3 w-3 inline mr-1" />
                      Suggested order: <strong>{suggestion.data.suggested_quantity}</strong>{" "}
                      {suggestion.data.unit} from{" "}
                      <strong>{suggestion.data.supplier_name}</strong>
                    </p>
                    {suggestion.data.estimated_total > 0 && (
                      <p className="text-xs text-slate-400 mt-1">
                        Est. cost: R {suggestion.data.estimated_total.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Link to="/purchase-orders">
          <Button
            className="w-full mt-4 rounded-sm bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-po-from-suggestions"
          >
            Create Purchase Order
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default AISuggestions;
